import React from 'react';
import { Panel, Button, EmptyState } from '../ui';
import { formatCurrency, formatTime } from './utils';

const COMPONENT_DEFS = [
  {
    id: 'kpi-summary',
    title: 'KPI Summary',
    description: 'Key performance indicators overview',
    adminOnly: false,
  },
  {
    id: 'project-analysis',
    title: 'Project Analysis',
    description: 'Project performance and health metrics',
    adminOnly: false,
  },
  {
    id: 'team-performance',
    title: 'Team Performance',
    description: 'Individual and team productivity data',
    adminOnly: true,
  },
  {
    id: 'time-trends',
    title: 'Time Trends',
    description: 'Historical time tracking patterns',
    adminOnly: false,
  },
  {
    id: 'financial-summary',
    title: 'Financial Summary',
    description: 'Revenue and billing information',
    adminOnly: true,
  },
];

const CustomReportsDashboard = ({
  kpis,
  projectMetrics = [],
  currencyCode = 'USD',
  teamMetrics = [],
  dailyTrends = [],
  timeEntries = [],
  selectedReportComponents = [],
  reportName,
  setReportName,
  showReportPreview,
  setShowReportPreview,
  addComponentToReport,
  removeComponentFromReport,
  clearReport,
  exportReport,
  user,
}) => {
  const filteredComponents =
    user?.role === 'admin'
      ? COMPONENT_DEFS
      : COMPONENT_DEFS.filter((component) => !component.adminOnly);

  return (
    <div className="space-y-6" data-testid="custom-reports-dashboard">
      <Panel>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Custom Report Builder</h3>
            <p className="text-secondary-600 mt-1">
              Build your own reports by selecting the metrics you need.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Report name..."
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
              data-testid="custom-report-name"
            />
            <Button
              variant="primary"
              disabled={selectedReportComponents.length === 0}
              onClick={() => setShowReportPreview(!showReportPreview)}
            >
              {showReportPreview ? 'Hide Preview' : 'Preview Report'}
            </Button>
            <Button variant="secondary" onClick={clearReport}>
              Clear All
            </Button>
            <Button
              variant="primary"
              disabled={selectedReportComponents.length === 0}
              onClick={exportReport}
              data-testid="custom-report-export"
            >
              Export JSON
            </Button>
          </div>
        </div>

        {selectedReportComponents.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-2">
              Selected Components ({selectedReportComponents.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedReportComponents.map((component) => {
                const info = COMPONENT_DEFS.find((c) => c.id === component.type);
                return (
                  <div
                    key={component.id}
                    className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-blue-200"
                  >
                    <span className="text-xs text-blue-700">{info?.title || component.type}</span>
                    <button
                      type="button"
                      onClick={() => removeComponentFromReport(component.id)}
                      className="text-red-500 hover:text-red-700 text-xs"
                      aria-label={`Remove ${info?.title}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredComponents.map((component) => {
          const count = selectedReportComponents.filter((c) => c.type === component.id).length;
          return (
            <Panel key={component.id}>
              <h4 className="font-semibold text-secondary-900 mb-1">{component.title}</h4>
              <p className="text-sm text-secondary-600 mb-4">{component.description}</p>
              <Button
                variant={count > 0 ? 'primary' : 'secondary'}
                onClick={() => addComponentToReport(component.id)}
                data-testid={`add-component-${component.id}`}
              >
                {count > 0 ? `Added (${count})` : 'Add to Report'}
              </Button>
            </Panel>
          );
        })}
      </div>

      {showReportPreview && selectedReportComponents.length > 0 && (
        <Panel data-testid="custom-report-preview">
          <h3 className="text-lg font-semibold mb-4">Report Preview</h3>
          <div className="space-y-4">
            {selectedReportComponents.map((component) => {
              if (component.type === 'kpi-summary') {
                return (
                  <div key={component.id} className="p-4 border border-secondary-200 rounded-lg">
                    <h4 className="font-semibold mb-3">KPI Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="font-bold text-blue-600">
                          {(kpis.totalHours || 0).toFixed(1)}h
                        </div>
                        <div className="text-sm">Total Hours</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="font-bold text-green-600">
                          {(kpis.billablePercentage || 0).toFixed(1)}%
                        </div>
                        <div className="text-sm">Billable</div>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="font-bold text-purple-600">{timeEntries.length}</div>
                        <div className="text-sm">Entries</div>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <div className="font-bold text-orange-600">{kpis.totalProjects}</div>
                        <div className="text-sm">Projects</div>
                      </div>
                    </div>
                  </div>
                );
              }
              if (component.type === 'project-analysis') {
                return (
                  <div key={component.id} className="p-4 border border-secondary-200 rounded-lg">
                    <h4 className="font-semibold mb-3">Project Analysis</h4>
                    {projectMetrics.slice(0, 5).map((project) => (
                      <div
                        key={project.id}
                        className="flex justify-between p-2 bg-secondary-50 rounded mb-2"
                      >
                        <span>{project.name}</span>
                        <span>
                          {(project.completionRate || 0).toFixed(1)}% •{' '}
                          {formatTime(project.totalTime)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (component.type === 'team-performance') {
                return (
                  <div key={component.id} className="p-4 border border-secondary-200 rounded-lg">
                    <h4 className="font-semibold mb-3">Team Performance</h4>
                    {teamMetrics.slice(0, 5).map((member) => (
                      <div
                        key={member.id}
                        className="flex justify-between p-2 bg-secondary-50 rounded mb-2"
                      >
                        <span>{member.full_name || member.username}</span>
                        <span>{(member.productivity || 0).toFixed(1)}h</span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (component.type === 'financial-summary') {
                return (
                  <div key={component.id} className="p-4 border border-secondary-200 rounded-lg">
                    <h4 className="font-semibold mb-3">Financial Summary</h4>
                    <div className="text-lg font-bold text-green-600">
                      {formatCurrency(kpis.totalRevenue, currencyCode)}
                    </div>
                    <div className="text-sm text-secondary-600">
                      {(kpis.billableHours || 0).toFixed(1)} billable hours
                    </div>
                  </div>
                );
              }
              if (component.type === 'time-trends') {
                const total = dailyTrends.reduce((s, d) => s + d.totalHours, 0);
                return (
                  <div key={component.id} className="p-4 border border-secondary-200 rounded-lg">
                    <h4 className="font-semibold mb-3">Time Trends</h4>
                    <div className="text-lg font-bold text-blue-600">{total.toFixed(1)}h</div>
                    <div className="text-sm text-secondary-600">
                      across {dailyTrends.length} days
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </Panel>
      )}

      {selectedReportComponents.length === 0 && (
        <EmptyState
          title="No components selected"
          description="Add one or more blocks above to build a custom report."
        />
      )}
    </div>
  );
};

export default CustomReportsDashboard;
